import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Button, Container, Form, Message, Modal, Dropdown, Dimmer, Loader } from 'semantic-ui-react';
import { CreateCheckListDto, NewCheckListStore } from 'stores/configuration/events-n-requests/checklists-store';
import { nameof } from 'utils/object';
import EventTypeEditor from 'widgets/bussiness/event-type-editor';
import { ItemReference } from 'stores/dataStore';

interface NewCheckListViewProps extends WithTranslation {
  onClose: (isSuccess: boolean) => void;
  newCheckList?: NewCheckListStore;
}

interface NewCheckListViewState {
  eventType: ItemReference;
  userClickedSaved: boolean;
}

@connect(['newCheckList', NewCheckListStore])
class NewCheckListView extends React.Component<NewCheckListViewProps, NewCheckListViewState> {
  private get newCheckListStore() {
    return this.props.newCheckList;
  }

  constructor(props: NewCheckListViewProps) {
    super(props);
    this.newCheckListStore.createNew({
      description: '',
      eventTypeId: null,
      rolesThatCreateCheckLists: [],
      isCheckListNew: false
    });
    this.state = {
      eventType: null,
      userClickedSaved: false
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: NewCheckListViewProps) {
    if (this.newCheckListStore.state.result && this.newCheckListStore.state.result.isSuccess) {
      nextProps.onClose(true);
    }
  }
  @autobind
  private onInstructorChecklistChange(isCheckListNew: boolean) {
    this.setState({ userClickedSaved: false });
    this.newCheckListStore.change({
      ...this.newCheckListStore.state.item,
      isCheckListNew
    });
  }

  private isInstructorOnly(roles: string[] | null | undefined): boolean {
    return roles?.length === 1 && roles[0] === 'Instructor';
  }

  private isInstructorOnlyEventType(eventTypeName: string | null | undefined): boolean {
    return ['TECHNICAL SUPPORT', 'TECHNICAL TRAINING', 'CONVERSION', 'RELOCATION'].includes((eventTypeName || '').trim().toUpperCase());
  }

  private canConfigureInstructorChecklist(): boolean {
    return (
      this.isInstructorOnly(this.newCheckListStore.state.item?.rolesThatCreateCheckLists) &&
      this.isInstructorOnlyEventType(this.state.eventType?.title)
    );
  }

  @autobind
  private onRolesChange(rolesThatCreateCheckLists: string[]) {
    if (this.isInstructorOnly(rolesThatCreateCheckLists) && this.isInstructorOnlyEventType(this.state.eventType?.title)) {
      this.handleValue(nameof<CreateCheckListDto>('rolesThatCreateCheckLists'), rolesThatCreateCheckLists);
      return;
    }

    this.setState({ userClickedSaved: false });
    this.newCheckListStore.change({
      ...this.newCheckListStore.state.item,
      rolesThatCreateCheckLists,
      isCheckListNew: false
    });
  }

  @autobind
  private onEventTypeChange(eventType: ItemReference) {
    const canConfigure =
      this.isInstructorOnly(this.newCheckListStore.state.item?.rolesThatCreateCheckLists) &&
      this.isInstructorOnlyEventType(eventType?.title);
    const item = {
      ...this.newCheckListStore.state.item,
      eventTypeId: eventType?.id,
      ...(canConfigure ? {} : { isCheckListNew: false })
    };

    this.setState({ eventType, userClickedSaved: false });
    this.newCheckListStore.change(item);
  }

  @autobind
  private onCreateNewItem() {
    this.setState({ userClickedSaved: true });
    this.newCheckListStore.submit();
  }

  @autobind
  private onCancelNewItem() {
    this.newCheckListStore.clear();
    this.props.onClose(false);
  }

  @autobind
  private handleValue(property: string, value: any) {
    this.setState({ userClickedSaved: false });
    const change = {};
    change[property] = value;

    this.newCheckListStore.change({ ...this.newCheckListStore.state.item, ...change });
  }

  public render() {
    const { t } = this.props as any;
    const { result } = this.newCheckListStore.state;
    const canConfigureInstructorChecklist = this.canConfigureInstructorChecklist();

    const roles = [
      { name: 'Employee', description: 'Employee' },
      { name: 'Planner', description: 'Planner TFT' },
      { name: 'PoC', description: 'PoC' },
      { name: 'Instructor', description: 'Instructor' },
      { name: 'PlannerMTC', description: 'Planner MTC' }
    ];

    return (
      <Modal open closeOnEscape={true} onClose={this.onCancelNewItem} closeOnDimmerClick={false}>
        <Dimmer active={this.newCheckListStore.state.isBusy} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('')}</Loader>
        </Dimmer>
        <Modal.Header className="modal__header">{t('New Check List')}</Modal.Header>
        <Modal.Content image>
          <Container className="configuration-event-n-request__modal">
            {this.newCheckListStore.state.result && !this.newCheckListStore.state.result.isSuccess && (
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.newCheckListStore.state.result.messages.map(o => o.body)}
              />
            )}
            {this.newCheckListStore.state.item && (
              <Form style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', flexBasis: '100%', gap: 12, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  <div className="required field inline configuration-event-n-request__modal__element">
                    <label>{t('Description')}</label>
                    <Form.Input
                      fluid
                      error={!this.newCheckListStore.state.item.description && this.state.userClickedSaved}
                      placeholder={t('Description')}
                      value={this.newCheckListStore.state.item.description}
                      onChange={(e, { value }) => this.handleValue(nameof<CreateCheckListDto>('description'), value)}
                    />
                  </div>

                  <div className={`required field inline configuration-event-n-request__modal__element`}>
                    <label>{t('Event Type')}</label>
                    <EventTypeEditor
                      error={!this.newCheckListStore.state.item.eventTypeId && this.state.userClickedSaved}
                      placeholder={t('Event Type')}
                      nullable
                      value={this.state.eventType}
                      onChange={this.onEventTypeChange}
                      directOnly={false}
                    />
                  </div>

                  <div className={`required field inline configuration-event-n-request__modal__element`}>
                    <label>{t('Roles')}</label>
                    <Dropdown
                      placeholder={t('Roles')}
                      search
                      selection
                      multiple
                      closeOnChange
                      closeOnBlur
                      closeOnEscape
                      selectOnNavigation
                      error={
                        result &&
                        !result.isSuccess &&
                        this.state.userClickedSaved &&
                        (result.messages || []).any(x => x.propertyName === nameof<CreateCheckListDto>('rolesThatCreateCheckLists'))
                      }
                      options={roles.map(x => ({
                        key: `rolesThatCreateCheckLists-${x.name}`,
                        text: x.description,
                        value: x.name,
                        content: x.description
                      }))}
                      value={this.newCheckListStore.state.item.rolesThatCreateCheckLists}
                      onChange={(e, { value }) => this.onRolesChange(value as string[])}
                    />
                  </div>
                </div>
                {canConfigureInstructorChecklist && (
                  <div style={{ flexBasis: '100%', marginTop: 12 }}>
                    <Form.Checkbox
                      label={t('Instructor checklist')}
                      checked={this.newCheckListStore.state.item.isCheckListNew}
                      onChange={(_, { checked }) => this.onInstructorChecklistChange(!!checked)}
                    />
                  </div>
                )}
              </Form>
            )}
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onCancelNewItem} basic>
            {t('Cancel')}
          </Button>
          <Button onClick={this.onCreateNewItem} positive>
            {t('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(NewCheckListView);
